// ═══════════════════════════════════════════════════════════════════════════
// geometria.js — Helpers geométricos para cruzar puntos con polígonos
// ───────────────────────────────────────────────────────────────────────────
// Usado por el dashboard Estrategia Flex para determinar si una venta
// (lat/lng del receiver_address) cae dentro del polígono de una zona Flex.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Algoritmo ray-casting clásico para point-in-polygon en 2D.
 *
 * @param {[number, number]} point   [lng, lat] (ojo: GeoJSON usa lng-lat, no lat-lng)
 * @param {Array<[number, number]>} polygon  Anillo de coordenadas [lng, lat]
 * @returns {boolean}
 */
export function pointInPolygon(point, polygon) {
  if (!Array.isArray(point) || point.length < 2) return false
  if (!Array.isArray(polygon) || polygon.length < 3) return false

  const x = Number(point[0]), y = Number(point[1])
  if (!Number.isFinite(x) || !Number.isFinite(y)) return false

  let inside = false
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = Number(polygon[i][0]), yi = Number(polygon[i][1])
    const xj = Number(polygon[j][0]), yj = Number(polygon[j][1])
    const intersect = ((yi > y) !== (yj > y))
      && (x < ((xj - xi) * (y - yi)) / (yj - yi || Number.EPSILON) + xi)
    if (intersect) inside = !inside
  }
  return inside
}

/**
 * Soporta GeoJSON Geometry de tipo Polygon o MultiPolygon.
 * Resta hoyos (anillos interiores).
 *
 * @param {[number, number]} point
 * @param {object} geometry  GeoJSON geometry (Polygon|MultiPolygon|Feature.geometry)
 * @returns {boolean}
 */
export function pointInGeoJSON(point, geometry) {
  if (!point || !geometry) return false
  // Si llega un Feature, agarrar su geometry
  const geom = geometry.geometry || geometry
  if (!geom || !geom.type || !geom.coordinates) return false

  if (geom.type === 'Polygon') {
    // coordinates[0] = anillo externo; coordinates[1..N] = hoyos
    const rings = geom.coordinates
    if (!rings || rings.length === 0) return false
    if (!pointInPolygon(point, rings[0])) return false
    // Si está en algún hoyo, no cuenta
    for (let i = 1; i < rings.length; i++) {
      if (pointInPolygon(point, rings[i])) return false
    }
    return true
  }

  if (geom.type === 'MultiPolygon') {
    for (const polygon of geom.coordinates) {
      const rings = polygon
      if (!rings || rings.length === 0) continue
      if (!pointInPolygon(point, rings[0])) continue
      let enHoyo = false
      for (let i = 1; i < rings.length; i++) {
        if (pointInPolygon(point, rings[i])) { enHoyo = true; break }
      }
      if (!enHoyo) return true
    }
    return false
  }

  return false
}

/**
 * Devuelve la primera zona cuyo polígono contiene el punto. O null.
 *
 * @param {[number, number]} point  [lng, lat]
 * @param {Array<{ id: string, polygon: object, ... }>} zones
 * @returns {object|null}
 */
export function findZonaForPoint(point, zones) {
  if (!Array.isArray(zones)) return null
  for (const z of zones) {
    if (!z) continue
    const geom = z.polygon || z.geometry || z
    if (pointInGeoJSON(point, geom)) return z
  }
  return null
}
